import React, { useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { LAST_CHANCE } from '../content-types';
import { trackContainerClick } from '../analytics';
import { useTranslator, useUrlLocalizer } from 'tradera/lang/translate';
import { registerLiveUpdates } from 'tradera/utils/register-live-updates';
import * as itemActions from 'tradera/state/items/actions';
import uiActions from 'tradera/state/ui/actions';
import { generateItemCardPlaceholders } from 'tradera/components/item-card/generate-item-card-placeholders';
import { ItemCard } from 'tradera/components/item-card/item-card';
import type { PossibleItemTypes } from 'tradera/components/item-card/item-card';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from 'tradera/state/language/selectors';
import { selectWebLiveUrl } from 'tradera/state/environment/selectors';
import type { ItemCardViewModel } from 'tradera/state/services/types/webapi-discover-generated';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import {
    getLastChanceQueryParams,
    useGetLastChanceQuery,
    updateQueryData
} from 'tradera/state/services/discover-api';
import { StartPageSection } from '../start-page-section/start-page-section';
import { StartPageSectionTitle } from '../start-page-section/start-page-section-title';
import { MoreResultsCard } from 'tradera/components/carousel/more-results-card/more-results-card';
import { Carousel } from 'tradera/components/carousel/carousel';
import { CarouselContent } from 'tradera/components/carousel/carousel-content';
import {
    LeftButton,
    RightButton
} from 'tradera/components/carousel/carousel-buttons';

type SocketItemEvent = {
    Type: string;
    Event: { AuctionId: number; BidAmount: number; NumberOfBids: number };
};

export const LastChance = () => {
    const dispatch = useAppDispatch();
    const webLiveUrl = useAppSelector(selectWebLiveUrl);
    const isStartPageLiveUpdatesEnabled = useIsFeatureEnabled(
        'start-page-live-updates'
    );
    const isMobile = useIsMobile();
    const userLanguage = useSelector(selectPreferredLanguageCode);
    const automaticTranslationPreferred = useSelector(
        selectAutomaticTranslationPreferred
    );
    const shippingCountryCode = useSelector(
        selectShippingRegionCountryCodeIso2
    );

    const queryParams = useMemo(
        () =>
            getLastChanceQueryParams(
                userLanguage,
                shippingCountryCode,
                automaticTranslationPreferred
            ),
        [userLanguage, shippingCountryCode, automaticTranslationPreferred]
    );

    const {
        data,
        error,
        isLoading: loading
    } = useGetLastChanceQuery(queryParams);
    const items = useMemo(() => data?.items ?? [], [data]);

    const openQuickView = useCallback(
        (item: ItemCardViewModel, items: ItemCardViewModel[]) =>
            dispatch(
                uiActions.openQuickViewAction({
                    items,
                    item,
                    openedFrom: 'Last Chance'
                })
            ),
        [dispatch]
    );

    const updatePriceAndBids = useCallback(
        (itemId: number, price: number, totalBids: number) => {
            dispatch(
                updateQueryData('getLastChance', queryParams, (data) => {
                    const item = data.items?.find(
                        (item) => item.itemId === itemId
                    );
                    if (item) {
                        // eslint-disable-next-line better-mutation/no-mutation
                        item.price = price;
                        // eslint-disable-next-line better-mutation/no-mutation
                        item.totalBids = totalBids;
                    }
                })
            );
        },
        [dispatch, queryParams]
    );

    const toggleWishlist = useCallback(
        (itemId: number) => dispatch(itemActions.wishlist(itemId)),
        [dispatch]
    );

    useEffect(() => {
        if (isStartPageLiveUpdatesEnabled) {
            return registerLiveUpdates({
                url: webLiveUrl,
                onConnect: (socket: {
                    emit: (eventName: string, data: string) => void;
                }) => {
                    for (const itemId of items.map((x) => x.itemId)) {
                        socket.emit('join', `item:${itemId}`);
                    }
                },
                onClientEvent: (data: SocketItemEvent) => {
                    switch (data.Type) {
                        case 'ItemSold':
                        case 'ItemUnSold':
                            break;
                        case 'AuctionLeadingBidUpdated':
                        case 'IAuctionLeadingBidUpdated':
                            updatePriceAndBids(
                                data.Event.AuctionId,
                                data.Event.BidAmount,
                                data.Event.NumberOfBids
                            );
                            break;
                    }
                }
            });
        }
    }, [
        dispatch,
        isStartPageLiveUpdatesEnabled,
        webLiveUrl,
        items,
        updatePriceAndBids
    ]);

    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();

    const handleQuickView = useCallback(
        (item: PossibleItemTypes) => {
            openQuickView(item as ItemCardViewModel, items);
        },
        [items, openQuickView]
    );

    if (error || (!loading && items.length === 0)) return null;

    const displayItems = loading ? generateItemCardPlaceholders(4) : items;

    // To indicate that the list is scrollable on mobile we show 2.5 items
    const numberCarouselCards = isMobile ? 2.5 : 6;

    return (
        <StartPageSection>
            <StartPageSectionTitle
                inSpa
                href={toLocalizedUrl('/campaign/sista-chansen')}
                onClick={() =>
                    trackContainerClick(LAST_CHANCE, 'Last chance title')
                }>
                {t('lastChance_header')}
            </StartPageSectionTitle>
            <Carousel
                id="last-chance-carousel"
                numberItemsToShow={numberCarouselCards}
                numberItemsToScroll={numberCarouselCards}
                className="mx-md-3 mx-lg-2 mx-xl-0"
                padX>
                <LeftButton alignVerticallyToItemCards size="md" />
                <CarouselContent>
                    {displayItems?.map((item) => (
                        <ItemCard
                            // This is to make the parent container set grid gaps instead
                            // of individual cards keeping paddings and margins set of their own.
                            // Makes displaying cards in different situations more predictable.
                            className="p-0 m-0"
                            animatePriceLabel
                            item={item}
                            key={item.itemId}
                            onAddToWishlist={toggleWishlist}
                            onRemoveFromWishlist={toggleWishlist}
                            onQuickView={handleQuickView}
                            showTitleOnOneLine
                            gaCategory="Startpage last chance"
                        />
                    ))}
                    <MoreResultsCard
                        href={toLocalizedUrl('/campaign/sista-chansen')}
                        onClick={() =>
                            trackContainerClick(
                                LAST_CHANCE,
                                'Last chance show more'
                            )
                        }
                        label={t('lastChance_showMore')}
                    />
                </CarouselContent>
                <RightButton alignVerticallyToItemCards size="md" />
            </Carousel>
        </StartPageSection>
    );
};
